import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Bell } from 'lucide-react';
import MobileBottomNav from './MobileBottomNav';
import { useAuth } from '../lib/auth';

/**
 * Full-bleed mobile shell — used by all /m/* pages.
 * Provides:
 *  - Ambient background
 *  - Optional sticky page header (title + back button + right slot)
 *  - Bottom nav
 *  - Consistent max-width & safe-area padding
 */
export default function MobileShell({
  title, backTo, right, children, noHeader = false, headerAccent = 'light',
  onBack,
}) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5F1EB] via-[#EEE8DD] to-[#E9E5DE] pb-28">
      <div className="ambient-bg opacity-40"/>

      {!noHeader && (
        <header
          className={`sticky top-0 z-30 px-4 pt-[env(safe-area-inset-top)] pb-3 pt-3 flex items-center gap-3 backdrop-blur-xl ${
            headerAccent === 'dark' ? 'bg-slate-900/70 text-white' : 'bg-white/85 border-b border-white/60'
          }`}
        >
          {(backTo || onBack) && (
            <button
              onClick={() => (onBack ? onBack() : navigate(backTo))}
              className={`w-10 h-10 rounded-full grid place-items-center -ml-2 ${headerAccent === 'dark' ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
              data-testid="m-back-btn"
            >
              <ChevronLeft size={22}/>
            </button>
          )}
          {title && (
            <h1 className={`flex-1 font-serif text-xl leading-tight truncate ${headerAccent === 'dark' ? 'text-white' : ''}`}>{title}</h1>
          )}
          {right}
        </header>
      )}

      <div className="px-4 pt-2 relative z-10">{children}</div>

      <MobileBottomNav/>
    </div>
  );
}

// Common animated tap card
export function TapCard({ children, className = '', to, onClick, testid }) {
  const Cmp = to ? Link : 'button';
  const props = to ? { to } : { onClick };
  return (
    <Cmp
      {...props}
      data-testid={testid}
      className={`block w-full text-left rounded-2xl bg-white/85 border border-white/70 shadow-sm active:scale-[0.98] active:shadow transition-transform ${className}`}
    >
      {children}
    </Cmp>
  );
}

// Greeting header shown at top of Home
export function GreetingHeader() {
  const { user, workspace } = useAuth();
  const h = new Date().getHours();
  const greet = h < 6 ? 'Working late' : h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = (user?.name || '').split(' ')[0] || 'there';
  return (
    <div className="pt-3 pb-5 flex items-center justify-between">
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-0.5">{greet}</div>
        <h1 className="font-serif text-3xl leading-tight truncate">{firstName}</h1>
        {workspace === 'demo' && <div className="text-[10px] uppercase tracking-widest text-amber-700 mt-1 font-medium">Demo workspace</div>}
      </div>
      <Link to="/app/mobile-more" className="w-11 h-11 rounded-full border-2 border-white shadow-md overflow-hidden" data-testid="m-avatar">
        <img
          src={user?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'U')}&background=1A2530&color=fff`}
          alt="" className="w-full h-full object-cover"
        />
      </Link>
    </div>
  );
}
